<template>
  <div>
    <nav class="navbar" id="navbar">
      <div class="container relative flex flex-wrap items-center justify-between">
        <!-- Logo -->
        <router-link class="navbar-brand md:me-8" :to="{ name: 'index' }">
          <img
            src="../assets/images/logo.png"
            class="inline-block dark:hidden"
            alt="Logo"
          />
          <img
            src="../assets/images/logo.png"
            class="hidden dark:inline-block"
            alt="Logo"
          />
        </router-link>

        <!-- Right side: Language + Burger -->
        <div class="flex items-center gap-2 px-4 nav-icons lg_992:order-2 ms-auto md:ms-8">
          <!-- Language Switch -->
          <div class="inline-flex items-center gap-1 px-1 rounded-full bg-white/70 dark:bg-slate-900/70 backdrop-blur ring-1 ring-slate-200 dark:ring-slate-700 h-9">
            <button
              type="button"
              @click="setLang('ar')"
              class="px-3 text-xs font-semibold transition rounded-full h-7"
              :class="
                lang === 'ar'
                  ? 'bg-secondary/60 text-primary'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60'
              "
              aria-label="Arabic"
            >
              ع
            </button>

            <button
              type="button"
              @click="setLang('en')"
              class="px-3 text-xs font-semibold transition rounded-full h-7"
              :class="
                lang === 'en'
                  ? 'bg-secondary/60 text-primary'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-200/60 dark:hover:bg-slate-700/60'
              "
              aria-label="English"
            >
              EN
            </button>
          </div>

          <!-- Burger -->
          <button
            data-collapse="menu-collapse"
            type="button"
            class="inline-flex items-center collapse-btn ms-2 text-dark dark:text-white lg_992:hidden"
            aria-controls="menu-collapse"
            aria-expanded="false"
            @click="handler"
          >
            <span class="sr-only">Navigation Menu</span>
            <i class="mdi mdi-menu text-[24px]"></i>
          </button>
        </div>

        <!-- Menu -->
        <div
          class="navigation lg_992:order-1 lg_992:flex ms-auto"
          :class="{ hidden: !toggle }"
          id="menu-collapse"
        >
          <ul
            id="navbar-navlist"
            class="flex flex-col items-stretch w-full gap-2 mt-2 navbar-nav lg:flex-row lg:items-center lg:w-auto lg:gap-0 lg:mt-0"
          >
            <li class="nav-item ms-0">
              <router-link class="block px-3 py-2 nav-link lg:py-0" :to="{ name: 'index' }">
                {{ t("home") }}
              </router-link>
            </li>

            <li class="nav-item ms-0">
              <a
                class="block px-3 py-2 nav-link lg:py-0"
                href="#services"
                @click.prevent="goToSection('services')"
              >
                {{ t("services") }}
              </a>
            </li>

            <li class="nav-item ms-0">
              <a
                class="block px-3 py-2 nav-link lg:py-0"
                href="#about"
                @click.prevent="goToSection('about')"
              >
                {{ t("about") }}
              </a>
            </li>

            <li class="nav-item ms-0">
              <router-link class="block px-3 py-2 nav-link lg:py-0" :to="{ name: 'contact' }">
                {{ t("contact_us") }}
              </router-link>
            </li>

            <li class="block my-2 border-t lg:hidden border-white/20 dark:border-slate-800/50"></li>

            <!-- Auth buttons -->
            <li class="order-last nav-item ms-0 lg:order-none">
              <!-- ✅ if logged in -->
              <TopNav
                v-if="isLoggedIn"
                :lang="lang"
                :t="t"
                :compact="true"
                :is-collapsed="false"
              />

              <!-- ❌ if not logged in -->
              <router-link
                v-else
                :to="{ name: 'Login' }"
                class="h-10 w-full lg:w-auto px-4 text-[12px] tracking-wider inline-flex items-center justify-center font-medium rounded-md bg-primary text-white uppercase hover:bg-primary-light transition"
              >
                {{ t("login") }}
              </router-link>
            </li>

            <li class="nav-item ms-0 lg:ms-2" v-if="!isLoggedIn">
              <router-link
                :to="{ name: 'RegisterClient' }"
                class="h-10 w-full lg:w-auto px-4 text-[12px] tracking-wider inline-flex items-center justify-center font-medium rounded-md border border-primary text-primary bg-transparent uppercase hover:bg-secondary hover:text-black transition"
              >
                {{ t("signup") }}
              </router-link>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </div>
</template>

<script>
import { messages, getSavedLang, setAppLang } from "@/i18n";
import TopNav from "@/components/Auth/TopNav.vue";

export default {
  components: { TopNav },

  data() {
    return {
      toggle: false,
      lang: getSavedLang(),
      current: "",
      sectionTop: null,
      messages,

      isLoggedIn: false,
      dashboardUrl: "/account/profile",
    };
  },

  created() {
    // ✅ apply saved lang on mount (dir + lang)
    this.lang = setAppLang(this.lang);

    // ✅ detect login
    this.isLoggedIn = !!this.getCookie("auth_user");

    this._langHandler = (e) => {
      const next = (e?.detail || "ar").toLowerCase().startsWith("en") ? "en" : "ar";
      if (this.lang !== next) this.lang = next;
    };
    window.addEventListener("app:lang", this._langHandler, { passive: true });
    window.addEventListener("app:lang-changed", this._langHandler, { passive: true });

    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("scroll", this.onscroll, { passive: true });
  },

  unmounted() {
    if (this._langHandler) {
      window.removeEventListener("app:lang", this._langHandler);
      window.removeEventListener("app:lang-changed", this._langHandler);
    }
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("scroll", this.onscroll);
  },

  methods: {
    handler() {
      this.toggle = !this.toggle;
    },

    handleScroll() {
      const navbar = document.getElementById("navbar");
      if (!navbar) return;

      if (document.body.scrollTop >= 50 || document.documentElement.scrollTop >= 50) {
        navbar.classList.add("is-sticky");
      } else {
        navbar.classList.remove("is-sticky");
      }
    },

    getCookie(name) {
      const match = document.cookie.match(
        new RegExp(
          "(?:^|; )" +
            name.replace(/([.$?*|{}()[\]\\/+^])/g, "\\$1") +
            "=([^;]*)"
        )
      );
      return match ? decodeURIComponent(match[1]) : null;
    },

    onscroll() {
      document.querySelectorAll("section").forEach((section) => {
        this.sectionTop = section.offsetTop;
        if (pageYOffset >= this.sectionTop - 60) {
          this.current = section.getAttribute("id");
        }
      });
    },

    setLang(lang) {
      if (this.lang === lang) return;

      // ✅ persist + update html dir/lang
      this.lang = setAppLang(lang);
    },

    goToSection(id) {
      const hash = `#${id}`;
      this.toggle = false;

      // If not on home, navigate to home with hash (NO RELOAD)
      if (this.$route.path !== "/") {
        this.$router.push({ path: "/", hash });
        return;
      }

      // Already on home: scroll
      const el = document.querySelector(hash);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      } else {
        // fallback: set hash
        this.$router.replace({ hash });
      }
    },

    t(key) {
      return this.messages?.[this.lang]?.[key] ?? key;
    },
  },
};
</script>
